import React, { createContext, useContext } from 'react';
import { CareType, useConfigQuery } from '../graphQL';
import { Nullable } from '../types';

type HasCareOrg = {
  careFlows: {
    careType: CareType;
    referralRestrictedStates: string[];
    statesAvailable: string[];
  }[];
};

const stateHasCare = (
  careType: CareType,
  state: string,
  organization?: Nullable<HasCareOrg>
): boolean => {
  if (organization) {
    return organization.careFlows
      .filter(careFlow => careFlow.careType === careType)
      .flatMap(careFlow => {
        if (careFlow.referralRestrictedStates.length) return careFlow.referralRestrictedStates;
        return careFlow.statesAvailable;
      })
      .some(s => s.toUpperCase() === state.toUpperCase());
  }
  // If no organization, it's DTC, which is available in all 50 states + DC.
  return true;
};

export const ConfigProvider: React.FC = ({ children }) => {
  const { data } = useConfigQuery({ fetchPolicy: 'network-only', nextFetchPolicy: 'cache-first' });

  if (!data) return null;

  return (
    <Context.Provider
      value={{
        featureFlags: data.featureFlags,
        stateHasCare,
      }}
    >
      {children}
    </Context.Provider>
  );
};

type ConfigContext = {
  stateHasCare: (careType: CareType, state: string, organization?: Nullable<HasCareOrg>) => boolean;
  featureFlags: string[];
};

const Context = createContext<ConfigContext | null>(null);

export const useConfigContext = () => {
  const context = useContext(Context);
  if (!context) throw new Error('hook called outside of config context');
  return context;
};
